import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandL, LabelM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useDefaultStadiumBackground } from '@sorare/core/src/hooks/useDefaultStadiumBackground';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BackToCommon } from 'components/onboarding/FirstLineup/FirstLineupEdition/BackToCommon';
import { GameweekDate } from 'components/onboarding/GameweekDate';
import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';

import { FirstLineupPreview } from './FirstLineupPreview';
import type {
  FirstLineupEdition_currentUser,
  FirstLineupEdition_so5Leaderboard,
} from './__generated__/index.graphql';

const FirstLineupPreviewWrapper = styled.div`
  max-width: 640px;
`;
const MobileSeparator = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid var(--c-nd-50);

  @media ${tabletAndAbove} {
    display: none;
  }
`;

type Props = {
  currentUser?: FirstLineupEdition_currentUser | null;
  so5Leaderboard?: FirstLineupEdition_so5Leaderboard;
  onSubmit: () => void;
};
export const FirstLineupEdition = ({
  currentUser,
  so5Leaderboard,
  onSubmit,
}: Props) => {
  const { sport } = useSportContext();
  const isTabletAndAbove = useIsTabletAndAbove();
  const defaultStadiumBackground = useDefaultStadiumBackground(sport);

  const recommendedCards =
    currentUser?.myCart?.mainCartItems
      .map(({ card }) => card)
      .filter(r => isType(r, 'Card')) || [];

  return (
    <MarketGuidanceLayout
      cta={
        <Button
          fullWidth={!isTabletAndAbove}
          color="secondary"
          size="medium"
          onClick={onSubmit}
        >
          <FormattedMessage
            id="RecommendedPlayers.cta"
            defaultMessage="Checkout <grey>- {amount}</grey>"
            values={{
              grey: str => (
                <span style={{ color: 'var(--c-nd-500-opaque)' }}>{str}</span>
              ),
              amount: '10$',
            }}
          />
        </Button>
      }
    >
      <Vertical center={isTabletAndAbove} gap={2}>
        <StarBall />
        <Vertical center={isTabletAndAbove}>
          <BodyL>
            <FormattedMessage
              id="RecommendedPlayers.title"
              defaultMessage="Your first Sorare lineup for Game Week {n} ({gameweekDate})."
              values={{
                n: so5Leaderboard?.so5Fixture?.gameWeek,
                gameweekDate: (
                  <GameweekDate so5Fixture={so5Leaderboard?.so5Fixture} />
                ),
              }}
            />
          </BodyL>
        </Vertical>
        <Vertical gap={isTabletAndAbove ? 5 : 3}>
          <FirstLineupPreviewWrapper>
            <FirstLineupPreview
              cards={recommendedCards}
              backgroundUrl={
                currentUser?.anyUserSportProfile?.background?.pictureUrl ||
                defaultStadiumBackground
              }
            />
          </FirstLineupPreviewWrapper>
          <MobileSeparator />
          <Vertical center>
            <BrandL>Own Your Game</BrandL>
            <LabelM className="text-center">
              <FormattedMessage
                id="RecommendedPlayers.ownYourCard"
                defaultMessage="You own those cards forever and can sell them to other managers anytime."
              />
            </LabelM>
          </Vertical>
        </Vertical>
        <BackToCommon />
      </Vertical>
    </MarketGuidanceLayout>
  );
};

FirstLineupEdition.fragments = {
  currentUser: gql`
    fragment FirstLineupEdition_currentUser on CurrentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        background {
          id
          pictureUrl
        }
      }
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          card {
            slug
            ...FirstLineupPreview_card
          }
        }
      }
    }
    ${FirstLineupPreview.fragments.card}
  ` as TypedDocumentNode<FirstLineupEdition_currentUser>,
  so5Leaderboard: gql`
    fragment FirstLineupEdition_so5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
        gameWeek
        ...GameweekDate_so5Fixture
      }
    }
    ${GameweekDate.fragments.so5Fixture}
  ` as TypedDocumentNode<FirstLineupEdition_so5Leaderboard>,
};
